import React, { useEffect, useState } from 'react';
import {
  Dropdown,
  Grid,
  Icon,
  Input,
  Modal,
  Button,
  Dimmer,
  Loader
} from 'semantic-ui-react';
import * as S from './styles';
import { FieldLabel } from '../../styles';
import { put } from '../../services/request';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../services/notification';
import { isValidEmail } from '../../utils/emailValidator';
import { get, set } from '../../services/storage';

interface UpdateModalProps {
  estateName: string;
  notificationEmailList: NotificationEmailType[];
  estateMainEmail: string;
  estateResponsibleEmail: string;
  estateFinancialEmail: string;
  estateId: string;
}

type NotificationEmailType = {
  id?: string;
  email: string;
  category: string;
};

type AdditionalEmailItem = {
  email: string;
  categories: string[];
};

const categories = [
  { key: 'ANALYSIS', text: 'Análises', value: 'ANALYSIS' },
  { key: 'HIRING', text: 'Contratações', value: 'HIRING' },
  { key: 'CANCELEDS', text: 'Cancelamentos', value: 'CANCELEDS' },
  { key: 'CLAIMS', text: 'Sinistros', value: 'CLAIMS' },
  { key: 'RENEWALS', text: 'Renovações', value: 'RENEWALS' },
  { key: 'FIRE_INSURANCE', text: 'Seguro Incêndio', value: 'FIRE_INSURANCE' }
];

const VALID_CATEGORIES = [
  'ANALYSIS',
  'HIRING',
  'CANCELEDS',
  'CLAIMS',
  'RENEWALS',
  'FIRE_INSURANCE'
];

export const UpdateEstateDataModal = ({
  estateMainEmail,
  estateFinancialEmail,
  estateName,
  notificationEmailList,
  estateResponsibleEmail,
  estateId
}: UpdateModalProps) => {
  const [open, setOpen] = useState(get('hasUpdatedEmails') !== 'true');
  const [isEditing, setIsEditing] = useState(false);
  const [mainEmail, setMainEmail] = useState(estateMainEmail);
  const [financialEmail, setFinancialEmail] = useState(estateFinancialEmail);
  const [responsibleEmail, setResponsibleEmail] = useState(estateResponsibleEmail);
  const [additionalEmails, setAdditionalEmails] = useState<AdditionalEmailItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!notificationEmailList || notificationEmailList.length === 0) return;
    const groupedEmails: Record<string, string[]> = {};
    notificationEmailList.forEach(({ email, category }) => {
      if (['MAIN', 'FINANCIAL', 'RESPONSIBLE'].includes(category)) return;
      if (!VALID_CATEGORIES.includes(category)) return;
      if (!groupedEmails[email]) {
        groupedEmails[email] = [];
      }
      groupedEmails[email].push(category);
    });
    const parsedEmails: AdditionalEmailItem[] = Object.keys(groupedEmails).map(email => ({
      email,
      categories: groupedEmails[email]
    }));

    setAdditionalEmails(parsedEmails);
  }, [notificationEmailList]);

  const handleCategoryChange = (index: number, selectedCategories: string[]) => {
    const validCategories = selectedCategories.filter(cat =>
      VALID_CATEGORIES.includes(cat)
    );
    setAdditionalEmails(prev => {
      const newState = [...prev];
      newState[index] = {
        ...newState[index],
        categories: validCategories
      };
      return newState;
    });
  };

  const handleRemoveEmail = (index: number) => {
    setAdditionalEmails(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleAddEmail = () => {
    setAdditionalEmails(prev => [...prev, { email: '', categories: [] }]);
  };

  const validateEmails = (emails: string[]) => {
    let isValid = true;
    emails.forEach(email => {
      if (!isValidEmail(email)) {
        warningNotification('Atenção!', 'E-mail inválido: ' + email);
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSave = async () => {
    if (!mainEmail) {
      warningNotification('Atenção!', 'É necessário informar o E-mail Principal!');
      return;
    }
    if (!financialEmail) {
      warningNotification('Atenção!', 'É necessário informar o E-mail Financeiro!');
      return;
    }

    if (!responsibleEmail) {
      warningNotification('Atenção!', 'É necessário informar o E-mail do Responsável!');
      return;
    }

    const flattenedAdditional = additionalEmails.flatMap(item =>
      item.categories
        .filter(cat => VALID_CATEGORIES.includes(cat))
        .map(cat => ({
          email: item.email,
          category: cat
        }))
    );

    const payload = [
      ...flattenedAdditional,
      { email: mainEmail, category: 'MAIN' },
      { email: financialEmail, category: 'FINANCIAL' },
      { email: responsibleEmail, category: 'RESPONSIBLE' }
    ];

    const uniqueEmails = Array.from(new Set(payload.map(item => item.email)));
    if (!validateEmails(uniqueEmails)) {
      return;
    }

    try {
      setLoading(true);
      await put(`/estates/update-emails/${estateId}`, payload);
      successNotification('Sucesso!', 'E-mails atualizados com sucesso!');
      setOpen(false);
      set('hasUpdatedEmails', 'true');
    } catch (error) {
      console.error(error);
      dangerNotification('Erro!', 'Erro ao atualizar os e-mails!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Dimmer active={loading} inverted>
        <Loader />
      </Dimmer>

      <S.Header>
        <Modal.Header className="header">
          <S.Title>
            <Icon name="shield" /> IMPORTANTE
          </S.Title>
        </Modal.Header>
      </S.Header>

      <Modal.Content>
        <S.Title>Confira a lista de emails ativos em sua imobiliária!</S.Title>
        <S.Subtitle>
          Caso algum dos emails abaixo se encontre inativo, edite para incluir os
          endereços atualizados.
        </S.Subtitle>

        <S.Wrapper>
          <S.WrapperTitle>
            <S.Title>{estateName}</S.Title>
            {!isEditing && (
              <Button
                style={{ marginTop: 10 }}
                primary
                onClick={() => setIsEditing(true)}
              >
                <Icon name="pencil" />
                Habilitar Edição
              </Button>
            )}
          </S.WrapperTitle>

          <S.WrapperContent>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
              <S.WrapperField>
                <label>
                  E-mail Principal <span style={{ color: 'red' }}>*</span>
                </label>
                <Input
                  className="main"
                  value={mainEmail}
                  onChange={(e, { value }) => setMainEmail(value)}
                  disabled={!isEditing}
                />
              </S.WrapperField>

              <S.WrapperField>
                <label>
                  E-mail Responsável <span style={{ color: 'red' }}>*</span>
                </label>
                <Input
                  className="responsible"
                  value={responsibleEmail}
                  onChange={(e, { value }) => setResponsibleEmail(value)}
                  disabled={!isEditing}
                />
              </S.WrapperField>

              <S.WrapperField>
                <label>
                  E-mail Financeiro <span style={{ color: 'red' }}>*</span>
                </label>
                <Input
                  className="financial"
                  value={financialEmail}
                  onChange={(e, { value }) => setFinancialEmail(value)}
                  disabled={!isEditing}
                />
              </S.WrapperField>
            </div>

            <S.SectionTitle>
              <S.Title>E-mails Adicionais</S.Title>
              {isEditing && (
                <Button style={{ marginTop: 10 }} primary onClick={handleAddEmail}>
                  <Icon name="plus circle" />
                  Adicionar E-mail
                </Button>
              )}
            </S.SectionTitle>

            {additionalEmails.map((item, index) => (
              <Grid.Row stretched key={`additional_email_${index}`}>
                <S.AdditionalEmailWrapper>
                  <S.WrapperField>
                    <FieldLabel>E-mail</FieldLabel>
                    <Input
                      disabled={!isEditing}
                      placeholder="E-mail adicional"
                      value={item.email}
                      onChange={(e, data) => {
                        const { value } = data;
                        setAdditionalEmails(prev => {
                          const newState = [...prev];
                          newState[index] = {
                            ...newState[index],
                            email: value
                          };
                          return newState;
                        });
                      }}
                    />
                  </S.WrapperField>

                  <S.WrapperField>
                    <FieldLabel>Categorias</FieldLabel>
                    <Dropdown
                      disabled={!isEditing}
                      className="category"
                      clearable
                      multiple
                      selection
                      fluid
                      options={categories}
                      value={item.categories}
                      onChange={(e, { value }) => {
                        handleCategoryChange(index, value as string[]);
                      }}
                    />
                  </S.WrapperField>

                  {isEditing && (
                    <Icon
                      className="icon"
                      name="remove circle"
                      color="grey"
                      style={{ cursor: 'pointer' }}
                      size="large"
                      onClick={() => handleRemoveEmail(index)}
                    />
                  )}
                </S.AdditionalEmailWrapper>
              </Grid.Row>
            ))}
          </S.WrapperContent>
        </S.Wrapper>
      </Modal.Content>

      <Modal.Actions>
        <Button
          secondary
          basic
          onClick={() => {
            set('hasUpdatedEmails', 'true');
            setOpen(false);
          }}
        >
          Cancelar
        </Button>
        {isEditing && (
          <Button onClick={handleSave} color="green">
            Salvar
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};
